import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ErrorIcon,
  FormItem,
  NullIcon,
  Paragraph,
  SelectList,
  Text,
} from 'elmo-elements';
import { format as formatDate } from 'date-fns';
import { DATETIME_FORMAT } from '@/app/const';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningDetailsQuery,
} from '@/modules/services';
import {
  isElearningPublishedToLive,
  isElearningPublishedToLiveNew,
  isElearningPublishedToSubdomain,
} from '@/lib/util';
import { SyncStatus } from '@/modules/types/elearning';

interface subdomainSelectListProps {
  elearningId: string;
  className?: string;
  defaultSubdomain: string;
  onChange: (selectedSubdomains: string[]) => void;
}

const SubdomainSelectList = ({
  elearningId,
  className,
  onChange,
  defaultSubdomain,
}: subdomainSelectListProps) => {
  const [selectionDomains, setSelectionDomains] = useState<Set<string>>(
    new Set(),
  );
  const { data: elearningData } = useGetElearningDetailsQuery(elearningId);
  const { data: tenantData } = useGetCurrentTenantProfileQuery();
  const subdomains = tenantData
    ? (tenantData.tenant.subdomain ?? '').trim().split('\n').filter(Boolean)
    : [];

  const getSubdomainPublishStateInfo = (subdomain: string) => {
    if (!elearningData) {
      return <NullIcon />;
    }

    const isLivePublished =
      isElearningPublishedToLive(+elearningData.statusMetadata.lmsStatus) &&
      isElearningPublishedToLiveNew(elearningData.statusMetadata);
    const isLiveUnsynced =
      +elearningData.statusMetadata.lmsSync === SyncStatus.unsynced;
    const isSubdomainPublished = isElearningPublishedToSubdomain(
      elearningData.statusMetadata,
      subdomain,
    );

    switch (true) {
      case !isLivePublished || !isSubdomainPublished:
        return (
          <>
            <Text color="gray" size="sm">
              <CheckCircleIcon size="sm" />
            </Text>
            <Text size="sm" className="subdomainText">
              Not published
            </Text>
          </>
        );

      case isLivePublished && isSubdomainPublished && !isLiveUnsynced:
        return (
          <>
            <Text color="success" size="sm">
              <CheckCircleIcon size="sm" />
            </Text>
            <Text size="sm" className="subdomainText">
              {elearningData.date.publishedLive &&
                `Last published ${formatDate(
                  new Date(elearningData.date.publishedLive),
                  DATETIME_FORMAT,
                )}`}
            </Text>
          </>
        );

      case isLivePublished && isSubdomainPublished && isLiveUnsynced:
        return (
          <>
            <Text color="warning" size="sm">
              <ErrorIcon size="sm" />
            </Text>
            <Text size="sm" className="subdomainText">
              {elearningData.date.publishedLive &&
                `Last published ${formatDate(
                  new Date(elearningData.date.publishedLive),
                  DATETIME_FORMAT,
                )}`}
            </Text>
          </>
        );

      default:
        return (
          <>
            <Text color="gray" size="sm">
              <CheckCircleIcon size="sm" />
            </Text>
            <Text size="sm" className="subdomainText">
              Not published
            </Text>
          </>
        );
    }
  };

  useEffect(() => {
    if (!elearningData || !tenantData) return;

    /* Pre-select subdomains already published. Blank entries must be dropped:
       publishedTo.includes('') is always true, so '' would pollute the selection. */
    (tenantData.tenant.subdomain ?? '')
      .split('\n')
      .filter(Boolean)
      .forEach((subdomain) => {
        if (
          isElearningPublishedToSubdomain(
            elearningData.statusMetadata,
            subdomain,
          )
        ) {
          setSelectionDomains((selection) => new Set(selection.add(subdomain)));
        }
      });
  }, [elearningData, tenantData]);

  /* Do not display anything as there is no selection necesary */
  if (subdomains.length <= 1) {
    return <div />;
  }

  return (
    <FormItem className={className} label="Select a subdomain">
      <SelectList
        onSelectAllToggle={() => {
          let selection: string[] = [];
          if (selectionDomains.size === subdomains.length) {
            onChange([defaultSubdomain]);
            setSelectionDomains(new Set([defaultSubdomain]));
          } else {
            selection = [...subdomains.map((subdomain) => subdomain)];
            onChange([...selection]);
            setSelectionDomains(new Set(selection));
          }
        }}
      >
        {subdomains.map((subdomain, index) => (
          <SelectList.Option
            key={`select-item-${index}`}
            isSelected={
              defaultSubdomain.includes(subdomain) ||
              selectionDomains.has(subdomain)
            }
            onToggle={() => {
              if (defaultSubdomain.includes(subdomain)) {
                return;
              }

              setSelectionDomains((selection) => {
                /* This is the flag we use to lock selection changes to already published subdomain */
                const isPublishedToSubdomain =
                  elearningData &&
                  isElearningPublishedToSubdomain(
                    elearningData.statusMetadata,
                    subdomain,
                  );

                if (selection.has(subdomain) && !isPublishedToSubdomain) {
                  selection.delete(subdomain);
                } else {
                  selection.add(subdomain);
                }

                if (onChange) {
                  onChange([...selection]);
                }

                return new Set(selection);
              });
            }}
          >
            <Paragraph className="subdomain-label">{subdomain}</Paragraph>
            {getSubdomainPublishStateInfo(subdomain)}
          </SelectList.Option>
        ))}
      </SelectList>
    </FormItem>
  );
};

export default SubdomainSelectList;
